import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const { Text, Paragraph } = Typography;

const PmvFuelAttentionPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>('OPEN');
  const [severity, setSeverity] = useState<string | undefined>();
  const [selected, setSelected] = useState<Record<string, any> | null>(null);

  const q = useQuery({
    queryKey: ['pmv-fuel-attention', status, severity],
    queryFn: () =>
      pmvService.listFuelAttention({
        status,
        limit: 100,
      }),
  });

  const items = useMemo(() => {
    const rows = q.data?.items ?? [];
    if (!severity) return rows;
    return rows.filter((r: { severity?: string }) => r.severity === severity);
  }, [q.data, severity]);

  const ack = useMutation({
    mutationFn: (id: string) => pmvService.ackFuelAttention(id),
    onSuccess: () => {
      message.success('Acknowledged');
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['pmv-fuel-attention'] });
      qc.invalidateQueries({ queryKey: ['pmv-fuel-control-tower'] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      pmvService.dismissFuelAttention(id, { dismiss_reason: 'Reviewed — no further action' }),
    onSuccess: () => {
      message.success('Dismissed');
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['pmv-fuel-attention'] });
      qc.invalidateQueries({ queryKey: ['pmv-fuel-control-tower'] });
    },
  });

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Fuel Attention"
        subtitle="Neutral, evidence-first — unexpected / unmatched / variance / requires review"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/fuel/control-tower')}>Control Tower</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel')}>Fuel Register</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/reconciliation')}>
              Reconciliation
            </Button>
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="Severity"
              style={{ width: 140 }}
              value={severity}
              onChange={setSeverity}
              options={['CRITICAL', 'WARNING', 'INFO'].map((v) => ({ value: v, label: v }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 140 }}
              value={status}
              onChange={setStatus}
              options={['OPEN', 'ACKNOWLEDGED', 'DISMISSED', 'RESOLVED'].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="System copy never defaults to theft, fraud, or misconduct. Human process decides next steps."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No fuel attention for these filters" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1100 }}
          tableLayout="fixed"
          onRow={(r) => ({
            onClick: () => setSelected(r),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Severity',
              dataIndex: 'severity',
              width: 100,
              render: (s) => (
                <Tag color={s === 'CRITICAL' ? 'red' : s === 'WARNING' ? 'orange' : 'blue'}>
                  {s}
                </Tag>
              ),
            },
            {
              title: 'Equipment',
              key: 'equipment',
              width: 200,
              ellipsis: true,
              render: (_: unknown, r: Record<string, any>) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/asset/pmv/${r.pmv_profile_id}?tab=fuel`);
                    }}
                  >
                    {r.equipment_label ||
                      r.fleet_number ||
                      r.asset_code ||
                      r.asset_name ||
                      'Open equipment'}
                  </Button>
                </div>
              ),
            },
            { title: 'Title', dataIndex: 'title', ellipsis: true },
            { title: 'Code', dataIndex: 'attention_code', width: 180, ellipsis: true },
            {
              title: 'Confidence',
              dataIndex: 'confidence',
              width: 110,
              render: (v) => v || '—',
            },
            {
              title: 'Age',
              dataIndex: 'last_seen_at',
              width: 160,
              render: (v) => (v ? new Date(v).toLocaleString() : '—'),
            },
            {
              title: 'Actions',
              width: 200,
              render: (_, r) => (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button size="small" onClick={() => ack.mutate(r.id)}>
                    Acknowledge
                  </Button>
                  <Button size="small" onClick={() => dismiss.mutate(r.id)}>
                    Dismiss
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      )}

      <Drawer
        title={selected?.title || 'Fuel attention'}
        open={!!selected}
        onClose={() => setSelected(null)}
        width={440}
        extra={
          selected ? (
            <Space>
              <Button size="small" onClick={() => ack.mutate(selected.id)}>
                Ack
              </Button>
              <Button size="small" danger onClick={() => dismiss.mutate(selected.id)}>
                Dismiss
              </Button>
            </Space>
          ) : null
        }
      >
        {selected ? (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Severity">
                <Tag>{selected.severity}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Code">{selected.attention_code}</Descriptions.Item>
              <Descriptions.Item label="Confidence">
                {selected.confidence || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Equipment">
                {selected.equipment_label || selected.asset_code || '—'}
              </Descriptions.Item>
            </Descriptions>
            <Paragraph style={{ marginTop: 16 }}>
              <Text strong>Message</Text>
              <br />
              {selected.message || selected.title || '—'}
            </Paragraph>
            {selected.evidence_json ? (
              <pre
                style={{
                  fontSize: 11,
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 6,
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(selected.evidence_json, null, 2)}
              </pre>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </WorkspaceLayout>
  );
};

export default PmvFuelAttentionPage;
